import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface JourneyStatus {
  journeyCompleted: boolean;
  isLoading: boolean;
  currentStepNumber: number | null;
  currentSection: string | null;
}

export const useJourneyStatus = (userId: string | undefined): JourneyStatus => {
  const { data, isLoading } = useQuery({
    queryKey: ['journey-status', userId],
    queryFn: async () => {
      if (!userId) return { 
        journeyCompleted: false, 
        currentStepNumber: null,
        currentSection: null
      };
      
      // Use select('*') and cast to avoid type issues with new columns
      const { data, error } = await supabase
        .from('user_journey_state')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching journey status:', error);
        return { 
          journeyCompleted: false, 
          currentStepNumber: null,
          currentSection: null
        };
      }
      
      // Get current active step
      const { data: progressData } = await supabase
        .from('user_journey_step_progress')
        .select('step_id, journey_steps(step_number, section_key)')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
      
      const currentStepNumber = (progressData?.journey_steps as any)?.step_number || 1;
      const currentSection = (progressData?.journey_steps as any)?.section_key || 
                            (data as any)?.current_section || 'admin';
      
      return { 
        journeyCompleted: (data as any)?.journey_completed || false,
        currentStepNumber,
        currentSection
      };
    },
    enabled: !!userId,
    staleTime: 30000,
  });
  
  return { 
    journeyCompleted: data?.journeyCompleted || false, 
    isLoading,
    currentStepNumber: data?.currentStepNumber || null,
    currentSection: data?.currentSection || null
  };
};
